import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useProgress } from '../contexts/ProgressContext';
import chaptersData from '../data/chapters.json';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import UserMenu from '../components/UserMenu';

const ChapterView = () => {
  const { day } = useParams();
  const navigate = useNavigate();
  const dayNumber = parseInt(day);
  
  const { 
    isDayUnlocked, 
    isChapterCompleted, 
    completeChapter,
    isQuizAttempted 
  } = useProgress();

  const [chapter, setChapter] = useState(null);
  const [showCompleteButton, setShowCompleteButton] = useState(true);

  useEffect(() => {
    // Check if day is unlocked
    if (!isDayUnlocked(dayNumber)) {
      navigate('/');
      return;
    }

    // Find chapter data
    const chapterData = chaptersData.find(c => c.day === dayNumber);
    if (chapterData) {
      setChapter(chapterData);
    }
  }, [dayNumber, isDayUnlocked, navigate]);

  const handleCompleteChapter = () => {
    completeChapter(dayNumber);
    setShowCompleteButton(false);
  };

  const handleStartQuiz = () => {
    navigate(`/quiz/${dayNumber}`);
  };

  if (!chapter) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading chapter...</div>
      </div>
    );
  }

  const isCompleted = isChapterCompleted(dayNumber);
  const quizAttempted = isQuizAttempted(dayNumber);
  const canTakeQuiz = isCompleted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-navy to-gray-900">
      {/* Navigation Bar */}
      <nav className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-teal hover:text-white transition-colors">
              ← Back to Dashboard
            </Link>
            <div className="text-white font-semibold">
              Day {dayNumber} of 30
            </div>
            <div className="flex gap-2">
              {dayNumber > 1 && (
                <Link 
                  to={`/chapter/${dayNumber - 1}`}
                  className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  ← Previous
                </Link>
              )}
              {dayNumber < 30 && isDayUnlocked(dayNumber + 1) && (
                <Link 
                  to={`/chapter/${dayNumber + 1}`}
                  className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Next →
                </Link>
              )}
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Chapter Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Chapter Header */}
        <div className="bg-gray-800 rounded-xl p-8 mb-8 shadow-xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-teal text-sm font-semibold mb-2">
                DAY {dayNumber} • PHASE {chapter.phase}
              </div>
              <h1 className="text-4xl font-bold text-white mb-2">
                {chapter.title}
              </h1>
            </div>
            {isCompleted && (
              <div className="bg-green-500 text-white px-4 py-2 rounded-full flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Completed
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl p-8 mb-8 shadow-xl prose prose-lg prose-slate max-w-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:text-gray-700 prose-p:leading-relaxed prose-strong:text-gray-900 prose-strong:font-semibold prose-table:border-collapse prose-th:border prose-th:border-gray-300 prose-th:bg-gray-100 prose-th:p-2 prose-td:border prose-td:border-gray-300 prose-td:p-2 prose-ul:list-disc prose-ol:list-decimal prose-li:text-gray-700 prose-code:text-sm prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{chapter.content}</ReactMarkdown>
        </div>

        {/* Disclaimer */}
        {chapter.disclaimer && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Disclaimer</h3>
            <p className="text-yellow-700">{chapter.disclaimer}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-gray-800 rounded-xl p-8 shadow-xl">
          <div className="flex flex-col gap-4">
            {/* Complete Chapter Button */}
            {!isCompleted && showCompleteButton && (
              <button
                onClick={handleCompleteChapter}
                className="w-full py-4 bg-gradient-to-r from-teal to-accent text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                ✓ Mark Chapter as Complete
              </button>
            )}

            {/* Quiz Button */}
            {canTakeQuiz && (
              <button
                onClick={handleStartQuiz}
                className={`w-full py-4 font-semibold rounded-lg transition-all duration-300 ${
                  quizAttempted
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gradient-to-r from-accent to-purple-500 text-white hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {quizAttempted ? '↻ Retake Quiz' : '→ Start Quiz'}
              </button>
            )}

            {!canTakeQuiz && (
              <div className="w-full py-4 bg-gray-700 text-gray-400 font-semibold rounded-lg text-center">
                🔒 Complete the chapter to unlock the quiz
              </div>
            )}
          </div>

          {/* Progress Info */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-gray-400 text-sm mb-1">Chapter Status</div>
                <div className="text-white font-semibold">
                  {isCompleted ? '✓ Completed' : 'In Progress'}
                </div>
              </div>
              <div>
                <div className="text-gray-400 text-sm mb-1">Quiz Status</div>
                <div className="text-white font-semibold">
                  {quizAttempted ? '✓ Attempted' : 'Not Started'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterView;
